import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ResponsiveDialog from '../dialog/dialog';
import SimpleBackdrop from '../loading/loading';
import authService from '../../service/auth/authService';

const theme = createTheme();

export default function ResetPassword() {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [open, setOpen] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [messageDialog, setMessageDialog] = useState('');
    const [titleDialog, setTitleDialog] = useState('');
    const [statusDialog, setStatusDialog] = useState('');
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [isValidating, setIsValidating] = useState(true);
    const [token, setToken] = useState('');

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const handleDialogOpen = () => {
        setOpenDialog(true);
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const validateToken = useCallback(async (tokenToValidate) => {
        setIsValidating(true);
        setOpen(true);

        try {
            const response = await authService.validateResetToken(tokenToValidate);
            
            setOpen(false);
            setIsValidating(false);

            if (response.success) {
                setIsTokenValid(true);
            } else {
                setIsTokenValid(false);
                handleDialogOpen();
                setTitleDialog('Token inválido');
                setMessageDialog(response.message || 'O token de redefinição de senha é inválido ou expirou.');
                setStatusDialog('error');
            }
        } catch (error) {
            console.error('Erro ao validar token:', error);
            setOpen(false);
            setIsValidating(false);
            setIsTokenValid(false);
            handleDialogOpen();
            setTitleDialog('Erro ao validar token');
            
            if (error.message && error.message !== 'Ocorreu um erro inesperado. Tente novamente.') {
                setMessageDialog(error.message);
            } else if (error.status === 404 || error.response?.status === 404) {
                setMessageDialog('Token não encontrado ou expirado.');
            } else if (error.status === 500 || error.response?.status === 500) {
                setMessageDialog('Erro interno do servidor. Tente novamente.');
            } else {
                setMessageDialog('Erro ao conectar com o servidor. Verifique sua conexão.');
            }
            
            setStatusDialog('error');
        }
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        if (!newPassword || !confirmPassword) {
            handleDialogOpen();
            setTitleDialog('Campos obrigatórios');
            setMessageDialog('Por favor, preencha todos os campos.');
            setStatusDialog('warning');
            return;
        }

        if (newPassword !== confirmPassword) {
            handleDialogOpen();
            setTitleDialog('Senhas não coincidem');
            setMessageDialog('As senhas informadas não são iguais. Por favor, verifique e tente novamente.');
            setStatusDialog('warning');
            return;
        }

        if (newPassword.length < 6) {
            handleDialogOpen();
            setTitleDialog('Senha muito curta');
            setMessageDialog('A senha deve ter no mínimo 6 caracteres.');
            setStatusDialog('warning');
            return;
        }

        if (!token) {
            handleDialogOpen();
            setTitleDialog('Token inválido');
            setMessageDialog('Token de redefinição não encontrado.');
            setStatusDialog('error');
            return;
        }

        setOpen(true);

        try {
            const response = await authService.resetPassword(token, newPassword);
            
            setOpen(false);
            
            if (response.success) {
                handleDialogOpen();
                setTitleDialog('Senha alterada com sucesso');
                setMessageDialog(response.message || 'Sua senha foi alterada com sucesso. Você já pode fazer login com a nova senha.');
                setStatusDialog('success');
                
                // Redirecionar para login após alguns segundos
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                handleDialogOpen();
                setTitleDialog('Erro ao alterar senha');
                setMessageDialog(response.message || 'Erro ao alterar a senha. Tente novamente.');
                setStatusDialog('error');
            }
        } catch (error) {
            console.error('Erro ao alterar senha:', error);
            setOpen(false);
            handleDialogOpen();
            setTitleDialog('Erro ao alterar senha');
            
            if (error.message && error.message !== 'Ocorreu um erro inesperado. Tente novamente.') {
                setMessageDialog(error.message);
            } else if (error.status === 404 || error.response?.status === 404) {
                setMessageDialog('Token não encontrado ou expirado.');
            } else if (error.status === 500 || error.response?.status === 500) {
                setMessageDialog('Erro interno do servidor. Tente novamente.');
            } else {
                setMessageDialog('Erro ao conectar com o servidor. Verifique sua conexão.');
            }
            
            setStatusDialog('error');
        }
    };

    useEffect(() => {
        // Pega o token da URL
        const tokenFromUrl = searchParams.get('token');
        
        if (!tokenFromUrl) {
            setIsValidating(false);
            handleDialogOpen();
            setTitleDialog('Token inválido');
            setMessageDialog('Token de redefinição de senha não encontrado na URL.');
            setStatusDialog('error');
            return;
        }

        setToken(tokenFromUrl);
        validateToken(tokenFromUrl);
    }, [searchParams, validateToken]);

    // Mostra loading enquanto valida o token
    if (isValidating) {
        return (
            <ThemeProvider theme={theme}>
                <Container component="main" style={{ padding: 60, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                    <SimpleBackdrop
                        open={true}
                        handleClose={() => {}}
                        title="Validando token..."
                    />
                </Container>
            </ThemeProvider>
        );
    }

    // Se o token não for válido, mostra mensagem de erro
    if (!isTokenValid) {
        return (
            <ThemeProvider theme={theme}>
                <Container component="main" style={{ padding: 60, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                    <ResponsiveDialog
                        open={openDialog}
                        handleClose={() => {
                            handleDialogClose();
                            navigate('/login');
                        }}
                        title={titleDialog}
                        message={messageDialog}
                        status={statusDialog}
                    />
                    <Card
                        sx={{
                            maxWidth: 500,
                            width: '100%',
                            boxShadow: '0 0 60px rgba(0, 0, 0, 0.1)',
                            borderRadius: '14px',
                        }}
                    >
                        <CardContent sx={{ padding: 4 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography component="h1" variant="h5" sx={{ marginBottom: 2, fontWeight: 'bold' }}>
                                    Token inválido
                                </Typography>
                                
                                <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 3, textAlign: 'center' }}>
                                    O link de redefinição de senha é inválido ou expirou. Por favor, solicite um novo link.
                                </Typography>

                                <Button
                                    variant="contained"
                                    onClick={() => navigate('/forgot-password')}
                                    sx={{
                                        mt: 2,
                                        backgroundColor: 'red',
                                        color: 'white',
                                        padding: '10px',
                                        borderRadius: '10px',
                                        '&:hover': {
                                            backgroundColor: '#d32f2f',
                                        },
                                    }}
                                >
                                    Solicitar novo link
                                </Button>

                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/login')}
                                    sx={{
                                        mt: 2,
                                        borderRadius: '10px',
                                        textTransform: 'none',
                                    }}
                                >
                                    Voltar para o login
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Container>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <Container component="main" style={{ padding: 60, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <SimpleBackdrop
                    open={open}
                    handleClose={handleClose}
                    title="Alterando senha..."
                />

                <ResponsiveDialog
                    open={openDialog}
                    handleClose={handleDialogClose}
                    title={titleDialog}
                    message={messageDialog}
                    status={statusDialog}
                />

                <Card
                    sx={{
                        maxWidth: 500,
                        width: '100%',
                        boxShadow: '0 0 60px rgba(0, 0, 0, 0.1)',
                        borderRadius: '14px',
                    }}
                >
                    <CardContent sx={{ padding: 4 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <Typography component="h1" variant="h5" sx={{ marginBottom: 2, fontWeight: 'bold' }}>
                                Redefinir senha
                            </Typography>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 3, textAlign: 'center' }}>
                                Digite sua nova senha abaixo.
                            </Typography>

                            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="newPassword"
                                    label="Nova senha"
                                    name="newPassword"
                                    type={showNewPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    autoFocus
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                    edge="end"
                                                    sx={{
                                                        '& svg': {
                                                            fontSize: '20px',
                                                        }
                                                    }}
                                                >
                                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                        },
                                    }}
                                />

                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="confirmPassword"
                                    label="Repita a nova senha"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    edge="end"
                                                    sx={{
                                                        '& svg': {
                                                            fontSize: '20px',
                                                        }
                                                    }}
                                                >
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                        },
                                    }}
                                />

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{
                                        mt: 3,
                                        mb: 2,
                                        backgroundColor: 'red',
                                        color: 'white',
                                        padding: '10px',
                                        borderRadius: '10px',
                                        '&:hover': {
                                            backgroundColor: '#d32f2f',
                                        },
                                    }}
                                >
                                    Alterar senha
                                </Button>

                                <Box sx={{ textAlign: 'center', mt: 2 }}>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => navigate('/login')}
                                        sx={{
                                            borderRadius: '10px',
                                            textTransform: 'none',
                                        }}
                                    >
                                        Voltar para o login
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </ThemeProvider>
    );
}
